import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Tag, RotateCcw } from "lucide-react";
import ProjectCard from "./ProjectCard";
import styles from "./Projects.module.css";

// Stagger container: orchestrates children timing
const containerVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.06, delayChildren: 0.04 },
  },
};

const SKELETON_HEIGHTS = [175, 235, 195, 160, 215, 185];

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState("All");
  const [selectedYear, setSelectedYear] = useState("All");
  const API_BASE = process.env.REACT_APP_API_BASE || (typeof window !== "undefined" && window.location?.hostname ? `http://${window.location.hostname}:3001` : "http://localhost:3001");

  useEffect(() => {
    // SWR: serve cache instantly, then refresh
    const cached = localStorage.getItem("projects");
    if (cached) {
      try {
        setProjects(JSON.parse(cached));
        setLoading(false);
      } catch (_) { }
    }

    fetch(`${API_BASE}/api/projects`)
      .then((res) => res.json())
      .then((data) => {
        setProjects(data);
        setLoading(false);
        localStorage.setItem("projects", JSON.stringify(data));
      })
      .catch((err) => {
        console.error("Failed to load projects:", err);
        setLoading(false);
      });
  }, [API_BASE]);

  // Extract unique Types & Years for filtering
  const availableTypes = useMemo(() => {
    const rawTypes = projects.map((p) => p.type || "App");
    const unique = Array.from(new Set(rawTypes)).filter(Boolean);
    // Ensure default core types are listed nicely if present
    return ["All", ...unique];
  }, [projects]);

  const availableYears = useMemo(() => {
    const rawYears = projects
      .map((p) => (p.date ? new Date(p.date).getFullYear().toString() : null))
      .filter(Boolean);
    const unique = Array.from(new Set(rawYears)).sort((a, b) => b - a);
    return ["All", ...unique];
  }, [projects]);

  // Filter projects by Type and Year
  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      const pType = p.type || "App";
      const matchesType = selectedType === "All" || pType === selectedType;

      const pYear = p.date
        ? new Date(p.date).getFullYear().toString()
        : "Unknown";
      const matchesYear = selectedYear === "All" || pYear === selectedYear;

      return matchesType && matchesYear;
    });
  }, [projects, selectedType, selectedYear]);

  const handleResetFilters = () => {
    setSelectedType("All");
    setSelectedYear("All");
  };

  const isFiltered = selectedType !== "All" || selectedYear !== "All";

  return (
    <div className={styles.galleryView}>
      {/* ── Gallery Filter Bar ── */}
      <div className={styles.galleryFilterBar}>
        {/* Left: Type Filter Chips */}
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>
            <Tag size={11} />
            Type:
          </span>
          {availableTypes.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setSelectedType(t)}
              className={`${styles.filterChip} ${
                selectedType === t ? styles.filterChipActive : ""
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Right: Year Filter & Counter */}
        <div className={styles.filterGroup}>
          {/* Year Dropdown */}
          <div className={styles.filterSelectWrap}>
            <span className={styles.filterLabel}>
              <Calendar size={11} />
              Year:
            </span>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className={styles.filterSelect}
              aria-label="Filter by Year"
            >
              <option value="All">All Years</option>
              {availableYears
                .filter((y) => y !== "All")
                .map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
            </select>
          </div>

          {/* Reset Filters button if active */}
          {isFiltered && (
            <button
              type="button"
              onClick={handleResetFilters}
              className="p-1 rounded text-gray-500 hover:text-red-500 hover:bg-gray-100 transition"
              title="Reset Filters"
            >
              <RotateCcw size={12} />
            </button>
          )}

          {/* Counter */}
          <span className={styles.filterCount}>
            {loading
              ? "Loading…"
              : `${filteredProjects.length} work${
                  filteredProjects.length !== 1 ? "s" : ""
                } on display`}
          </span>
        </div>
      </div>

      {/* Masonry Art Wall Grid */}
      <div className={styles.masonryWall}>
        {loading ? (
          // Shimmer skeleton cards
          SKELETON_HEIGHTS.map((h, i) => (
            <div key={i} className={styles.skeleton} style={{ height: h }} />
          ))
        ) : filteredProjects.length === 0 ? (
          <div className={styles.emptyState}>
            <span>🖼️</span>
            <p>No exhibition works match the selected filters.</p>
            {isFiltered && (
              <button
                type="button"
                onClick={handleResetFilters}
                className="mt-2 px-3 py-1 text-xs font-semibold bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition"
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <AnimatePresence mode="popLayout">
            <motion.div
              key={`${selectedType}-${selectedYear}`}
              variants={containerVariants}
              initial="hidden"
              animate="show"
              style={{ display: "contents" }}
            >
              {filteredProjects.map((p, index) => (
                <ProjectCard key={p._id} project={p} index={index} />
              ))}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
